import { z } from "zod";

const codePointLength = (value: string) => [...value].length;
const utf8ByteLength = (value: string) =>
  new TextEncoder().encode(value).length;

const jsonByteLength = (value: unknown) =>
  utf8ByteLength(JSON.stringify(value));

const normalizeHumanText = (value: string) => value.trim().normalize("NFC");

export const UuidSchema = z.string().uuid();

export const RoleSchema = z.enum(["ADMIN", "TEACHER", "STUDENT"]);
export type Role = z.infer<typeof RoleSchema>;

export const UserStatusSchema = z.enum(["ACTIVE", "DISABLED"]);
export type UserStatus = z.infer<typeof UserStatusSchema>;

export const ClassStatusSchema = z.enum(["ACTIVE", "ARCHIVED"]);
export type ClassStatus = z.infer<typeof ClassStatusSchema>;

export const CanvasStatusSchema = z.enum(["ACTIVE", "TRASHED", "PURGING"]);
export type CanvasStatus = z.infer<typeof CanvasStatusSchema>;

export const CanvasModeSchema = z.enum(["EDIT", "READ_ONLY"]);
export type CanvasMode = z.infer<typeof CanvasModeSchema>;

/**
 * CLASS                an ordinary canvas the whole class opens.
 * PRESENTATION         a presentation lesson (발표 수업): a teacher-made
 *                      parent with no drawing of its own.
 * PRESENTATION_STUDENT one student's slides inside a presentation lesson,
 *                      opened only by that student and the class's teacher.
 */
export const CanvasKindSchema = z.enum([
  "CLASS",
  "PRESENTATION",
  "PRESENTATION_STUDENT",
]);
export type CanvasKind = z.infer<typeof CanvasKindSchema>;

export const ClassCapabilitySchema = z.enum([
  "CLASS_READ",
  "CLASS_MANAGE",
  "ROSTER_READ",
  "STUDENT_MANAGE",
  "CANVAS_CREATE",
]);
export type ClassCapability = z.infer<typeof ClassCapabilitySchema>;

export const CanvasCapabilitySchema = z.enum([
  "CANVAS_READ",
  "CANVAS_EDIT",
  "CANVAS_MANAGE",
  "ASSET_READ",
  "ASSET_WRITE",
  "REVISION_READ",
  "REVISION_RESTORE",
]);
export type CanvasCapability = z.infer<typeof CanvasCapabilitySchema>;

export const LoginIdSchema = z
  .string()
  .transform((value) => value.trim().toLowerCase())
  .pipe(z.string().regex(/^[a-z0-9][a-z0-9._-]{2,63}$/));

export const PasswordSchema = z.string().superRefine((value, context) => {
  const length = codePointLength(value);
  if (length < 8 || length > 128) {
    context.addIssue({
      code: z.ZodIssueCode.custom,
      message: "Password must contain between 8 and 128 Unicode code points",
    });
  }
  if (value.includes("\0")) {
    context.addIssue({
      code: z.ZodIssueCode.custom,
      message: "Password must not contain NUL",
    });
  }
  if (utf8ByteLength(value) > 512) {
    context.addIssue({
      code: z.ZodIssueCode.custom,
      message: "Password must not exceed 512 UTF-8 bytes",
    });
  }
});

export const AccountPrefixSchema = z
  .string()
  .transform((value) => value.trim().toLowerCase())
  .pipe(z.string().regex(/^[a-z][a-z0-9-]{2,19}$/));

const normalizedHumanText = z.string().transform(normalizeHumanText);

export const TitleSchema = normalizedHumanText.pipe(
  z.string().superRefine((value, context) => {
    const length = codePointLength(value);
    if (length < 1 || length > 120) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Title must contain between 1 and 120 Unicode code points",
      });
    }
  }),
);

export const DisplayNameSchema = normalizedHumanText.pipe(
  z.string().superRefine((value, context) => {
    const length = codePointLength(value);
    if (length < 2 || length > 30) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        message:
          "Display name must contain between 2 and 30 Unicode code points",
      });
    }
  }),
);

export const StudentNumberSchema = z.number().int().min(1).max(30);

export const PrincipalSchema = z
  .object({
    userId: UuidSchema,
    role: RoleSchema,
    loginId: LoginIdSchema,
    initialPasswordActive: z.boolean(),
    sessionId: UuidSchema,
  })
  .strict();
export type Principal = z.infer<typeof PrincipalSchema>;

export const ClassGrantSchema = z
  .object({
    principal: PrincipalSchema,
    classId: UuidSchema,
    teacherUserId: UuidSchema,
    classStatus: ClassStatusSchema,
    studentNumber: StudentNumberSchema.nullable(),
    displayName: DisplayNameSchema.nullable(),
  })
  .strict();
export type ClassGrant = z.infer<typeof ClassGrantSchema>;

export const CanvasGrantSchema = z
  .object({
    principal: PrincipalSchema,
    classId: UuidSchema,
    canvasId: UuidSchema,
    mode: CanvasModeSchema,
    studentNumber: StudentNumberSchema.nullable(),
    displayName: DisplayNameSchema.nullable(),
  })
  .strict();
export type CanvasGrant = z.infer<typeof CanvasGrantSchema>;

export const AuditActionSchema = z.enum([
  "ADMIN_CREATED",
  "TEACHER_CREATED",
  "TEACHER_STATUS_CHANGED",
  "PASSWORD_CHANGED",
  "PASSWORD_RESET",
  "SESSION_REVOKED",
  "CLASS_CREATED",
  "CLASS_RENAMED",
  "CLASS_ARCHIVED",
  "CLASS_REACTIVATED",
  "STUDENT_NAME_SET",
  "STUDENT_NAME_CHANGED",
  "STUDENT_NAME_CLEARED",
  "STUDENT_STATUS_CHANGED",
  "CANVAS_CREATED",
  "CANVAS_RENAMED",
  "CANVAS_TRASHED",
  "CANVAS_RESTORED",
  "REVISION_RESTORED",
  "PURGE_STARTED",
  "PURGE_COMPLETED",
  "PURGE_FAILED",
  "BETA_APPLICATION_REJECTED",
  "BETA_APPLICATION_DELETED",
  "TEACHER_SCHOOL_SET",
  "GALLERY_POST_REMOVED",
  "GALLERY_POST_ALLOWED_AGAIN",
  "GALLERY_POST_HIDDEN",
  "GALLERY_POST_RESTORED",
  "GALLERY_POST_DELETED",
  "IMPERSONATION_STARTED",
  "IMPERSONATION_ENDED",
]);
export type AuditAction = z.infer<typeof AuditActionSchema>;

export type JsonValue =
  | null
  | boolean
  | number
  | string
  | JsonValue[]
  | { [key: string]: JsonValue };

// PostgreSQL JSONB rejects NUL and unpaired UTF-16 surrogates in strings and keys.
const unpairedSurrogate =
  /[\ud800-\udbff](?![\udc00-\udfff])|(?<![\ud800-\udbff])[\udc00-\udfff]/;

const isStorableText = (value: string): boolean =>
  !value.includes("\u0000") && !unpairedSurrogate.test(value);

const isPlainJsonValue = (
  value: unknown,
  ancestors: Set<object> = new Set(),
): value is JsonValue => {
  if (value === null || typeof value === "boolean") {
    return true;
  }
  if (typeof value === "string") {
    return isStorableText(value);
  }
  if (typeof value === "number") {
    return Number.isFinite(value);
  }
  if (typeof value !== "object" || ancestors.has(value)) {
    return false;
  }

  const prototype = Object.getPrototypeOf(value);
  if (Array.isArray(value)) {
    ancestors.add(value);
    const valid = value.every((item) => isPlainJsonValue(item, ancestors));
    ancestors.delete(value);
    return valid;
  }
  if (prototype !== Object.prototype && prototype !== null) {
    return false;
  }
  if (!Object.keys(value).every(isStorableText)) {
    return false;
  }

  ancestors.add(value);
  const valid = Object.values(value).every((item) =>
    isPlainJsonValue(item, ancestors),
  );
  ancestors.delete(value);
  return valid;
};

export const JsonValueSchema = z.custom<JsonValue>(isPlainJsonValue, {
  message: "Expected a finite, acyclic JSON value with a plain prototype",
});

export type SerializedExcalidrawElement = {
  id: string;
  type: string;
  version: number;
  versionNonce: number;
  updated: number;
  isDeleted: boolean;
  [key: string]: JsonValue;
};

const isSerializedExcalidrawElement = (
  value: unknown,
): value is SerializedExcalidrawElement => {
  if (
    !isPlainJsonValue(value) ||
    value === null ||
    Array.isArray(value) ||
    typeof value !== "object"
  ) {
    return false;
  }
  const element = value as Record<string, JsonValue>;
  return (
    typeof element.id === "string" &&
    element.id.length > 0 &&
    typeof element.type === "string" &&
    element.type.length > 0 &&
    typeof element.version === "number" &&
    Number.isInteger(element.version) &&
    element.version >= 0 &&
    typeof element.versionNonce === "number" &&
    Number.isInteger(element.versionNonce) &&
    typeof element.updated === "number" &&
    Number.isFinite(element.updated) &&
    typeof element.isDeleted === "boolean"
  );
};

export const SerializedExcalidrawElementSchema =
  z.custom<SerializedExcalidrawElement>(isSerializedExcalidrawElement, {
    message: "Expected a serialized Excalidraw element",
  });

export const SceneSchema = z.array(SerializedExcalidrawElementSchema);

export const MAX_SCENE_BYTES = 10 * 1024 * 1024;

export const createSceneSchema = (maximumBytes: number) =>
  SceneSchema.superRefine((value, context) => {
    if (jsonByteLength(value) > maximumBytes) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        message: `Scene exceeds ${maximumBytes} bytes`,
      });
    }
  });

export const BoundedSceneSchema = createSceneSchema(MAX_SCENE_BYTES);
